import {useEffect, useLayoutEffect, useRef, useState} from "react";

/**
 * Üç satırı birlikte otomatik sığdırır.
 * - width: 52mm sabit kutu
 * - white-space: nowrap -> satır hiç aşağı düşmez, font küçülür
 * - Hepsi aynı font-size (CSS var(--fs)) kullanır
 */
export default function AutoFitGroup({ processedData,widthMm = 52 }) {
  const { partzero, parttwo, partthree } = processedData || {};
  const boxRef = useRef(null);
  const [fontSize, setFontSize] = useState(12); // pt
  const MIN_FS = 6;   // ihtiyaca göre düşürebilirsiniz
  const MAX_FS = 12;  // tasarımınızın hedef fontu

  // Bir font boyutunda sığıyor mu?
  const fitsAt = (sizePt) => {
    const el = boxRef.current;
    if (!el) return true;

    // CSS değişkeni üzerinden tüm <p> aynı anda güncelleniyor
    el.style.setProperty("--fs", `${sizePt}pt`);

    // DOM güncellemelerinin tamamlanması için reflow tetikle
    void el.offsetHeight;

    // Container'ın gerçek genişliğini al
    const containerWidth = el.clientWidth;
    
    // Yanlardan 1mm boşluk için 2mm'yi pixel'e çevir (96 DPI'da 1mm ≈ 3.78px)
    // DPI'ya bağlı olmaması için container genişliğinden oransal olarak çıkarıyoruz
    // widthMm'den 2mm çıkarıp, o oranı kullanıyoruz
    const marginMm = 2; // Her iki yandan 1mm = toplam 2mm
    const availableWidth = containerWidth * ((widthMm - marginMm) / widthMm);
    
    let ok = true;
    // data-line atanmış tüm satırları tek tek kontrol et
    el.querySelectorAll("[data-line]").forEach((n) => {
      // Her satırın scrollWidth'ini kullanılabilir genişlik ile karşılaştır
      // Eğer scrollWidth kullanılabilir genişlikten büyükse taşıyor demektir
      if (n.scrollWidth > availableWidth) {
        ok = false;
      }
    });

    return ok;
  };

  // En büyük sığan fontu 0.1pt hassasiyetle bul (binary search)
  const recalc = () => {
    let lo = MIN_FS;
    let hi = MAX_FS;
    let best = MIN_FS;

    // İlk deneme: hedef font zaten sığıyorsa direkt onu kullan
    if (fitsAt(MAX_FS)) {
      setFontSize(MAX_FS);
      return;
    }

    for (let i = 0; i < 20; i++) { // ~0.00095 hassasiyet; pratikte yeterli
      const mid = (lo + hi) / 2;
      if (fitsAt(mid)) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
      if (hi - lo < 0.1) break; // 0.1pt yeterli çözünürlük
    }
    setFontSize(Math.max(MIN_FS, Math.min(MAX_FS, Math.round(best * 10) / 10)));
    // Son değer CSS değişkenine zaten yazıldı, state sadece yeniden render için
  };

  // İçerik veya genişlik değişince ya da ilk render'da hesapla
  useLayoutEffect(() => {
    recalc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partzero, parttwo, partthree, widthMm]);

  // Kutu boyutu değişirse (ör. zoom / DPI / resize) tekrar hesapla
  useEffect(() => {
    if (!boxRef.current) return;
    const ro = new ResizeObserver(() => recalc());
    ro.observe(boxRef.current);
    window.addEventListener("resize", recalc);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={boxRef}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        position: "absolute",
        top: 36,
        left: 0,
        width: `${widthMm}mm`,
        // tüm satırlara tek noktadan font boyutu
        // (JS ölçümler fitsAt içinde bu değişkeni güncelliyor)
        ["--fs"]: `${fontSize}pt`,
      }}
    >
      <p
        className="zara-regular"
        data-line
        style={{
          fontSize: "var(--fs)",
          whiteSpace: "nowrap",
          margin: 0,
        }}
      >
        {partzero}
      </p>

      <p
        className="zara-regular"
        data-line
        style={{
          fontSize: "var(--fs)",
          whiteSpace: "nowrap",
          marginTop: -3, // isterseniz 0 yapın, taşma kontrolünü kolaylaştırır
          marginBottom: 0,
        }}
      >
        {parttwo}
      </p>

      <p
        className="zara-regular"
        data-line
        style={{
          fontSize: "var(--fs)",
          whiteSpace: "nowrap",
          marginTop: -3,
          marginBottom: 0,
        }}
      >
        {partthree}
      </p>
    </div>
  );
}
